import './style.scss'
import { Button, List, Modal, Spin, Tabs } from 'antd'
import dayjs from 'dayjs'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Api from '@/api/account'
import { UserStore } from '@/global-states'
import PayPointModal from '../PayPointModal'

interface IProps {
  open: boolean
  onCancel?: () => void
}

interface CreditLog {
  change_type: number
  create_time: number
  credits_delta: number
  description: string
}

const PAGE_SIZE = 10

const CreditDetailModal: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['credit_detail_modal'])
  const { open, onCancel } = props
  const { userPackage } = UserStore
  const [payPointModalOpen, setPayPointModalOpen] = useState(false)
  const [creditLogs, setCreditLogs] = useState<CreditLog[]>([])
  const [tabIndex, setTabIndex] = useState('0')
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const totalPoint = useMemo(() => {
    if (!userPackage) return 0
    return (
      (userPackage.membership_credits || 0) + (userPackage.permanent_credits || 0) + (userPackage.free_credits || 0)
    )
  }, [userPackage])

  useEffect(() => {
    if (open) {
      UserStore.updateUserPackage()
      // 每次打开弹窗重置数据
      setCreditLogs([])
      setPage(0)
      setHasMore(true)
      loadMoreData(0, true)
    }
  }, [open])

  useEffect(() => {
    // 切换tab时重置数据
    setCreditLogs([])
    setPage(0)
    setHasMore(true)
    loadMoreData(0, true)
  }, [tabIndex])

  const toInvite = () => {
    window.open('/invite')
  }

  const toViewDesc = () => {
    window.open('https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0')
  }

  const loadMoreData = async (pageNum: number, reset?: boolean) => {
    if (loading) return

    setLoading(true)
    try {
      const res = await Api.getAllCreditLogs({
        page: pageNum,
        size: PAGE_SIZE,
        direction: +tabIndex
      })

      if (reset) {
        setCreditLogs(res?.list || [])
      } else {
        setCreditLogs((prev) => [...prev, ...(res?.list || [])])
      }

      setHasMore((res?.list?.length || 0) >= PAGE_SIZE)
      setPage(pageNum)
    } finally {
      setLoading(false)
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 20 && !loading && hasMore) {
      loadMoreData(page + 1)
    }
  }

  const renderLogItem = (item: CreditLog) => {
    const isIncrease = item.credits_delta > 0
    return (
      <div className="credit-log-item">
        <div className="log-content">
          <div className="log-description">{item.description}</div>
          <div className={`log-amount ${isIncrease ? 'increase gradient' : 'decrease'}`}>
            {isIncrease ? `+${item.credits_delta}` : item.credits_delta || '0'}
          </div>
        </div>
        <div className="log-time">{dayjs.unix(item.create_time).format('YYYY-MM-DD HH:mm:ss')}</div>
      </div>
    )
  }

  return (
    <>
      <Modal
        onCancel={onCancel}
        title={translate('credit_detail_modal.credit_details')}
        open={!!open}
        width={800}
        className="commom-modal credit-detail-modal"
        footer={
          <div className="credit-detail-footer">
            <div className="gradient" onClick={toViewDesc}>
              {translate('credit_detail_modal.view_credit_desc')}
            </div>
            <div className="gradient" onClick={toInvite}>
              {translate('credit_detail_modal.get_free_credits')}
            </div>
            <Button
              type="primary"
              onClick={() => {
                onCancel?.()
                setPayPointModalOpen(true)
              }}
            >
              {translate('credit_detail_modal.recharge_credits')}
            </Button>
          </div>
        }
      >
        <div className="credit-detail-main">
          <div className="header">
            <div className="credit-item">
              <div className="title">{translate('credit_detail_modal.remaining_credits')}</div>
              <div className="num gradient">{totalPoint || 0}</div>
            </div>
            <div className="credit-padding">=</div>
            <div className="credit-item">
              <div className="title">{translate('credit_detail_modal.membership_credits')}</div>
              <div className="num gradient">{userPackage?.membership_credits || 0}</div>
            </div>
            <div className="credit-padding">+</div>
            <div className="credit-item">
              <div className="title">{translate('credit_detail_modal.purchased_credits')}</div>
              <div className="num gradient">{userPackage?.permanent_credits || 0}</div>
            </div>
            <div className="credit-padding">+</div>
            <div className="credit-item">
              <div className="title">{translate('credit_detail_modal.free_credits')}</div>
              <div className="num gradient">{userPackage?.free_credits || 0}</div>
            </div>
          </div>

          <div className="content">
            <div className="tabs">
              <Tabs
                activeKey={tabIndex}
                items={[
                  {
                    key: '0',
                    label: translate('credit_detail_modal.all'),
                    children: (
                      <div className="log-list-container" onScroll={handleScroll}>
                        <List dataSource={creditLogs} renderItem={renderLogItem} loading={loading && page === 0} />
                        {loading && page > 0 && (
                          <div className="loading-more">
                            <Spin size="small" />
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    key: '1',
                    label: translate('credit_detail_modal.earned'),
                    children: (
                      <div className="log-list-container" onScroll={handleScroll}>
                        <List dataSource={creditLogs} renderItem={renderLogItem} loading={loading && page === 0} />
                        {loading && page > 0 && (
                          <div className="loading-more">
                            <Spin size="small" />
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    key: '2',
                    label: translate('credit_detail_modal.consumed'),
                    children: (
                      <div className="log-list-container" onScroll={handleScroll}>
                        <List dataSource={creditLogs} renderItem={renderLogItem} loading={loading && page === 0} />
                        {loading && page > 0 && (
                          <div className="loading-more">
                            <Spin size="small" />
                          </div>
                        )}
                      </div>
                    )
                  }
                ]}
                onChange={(key) => setTabIndex(key)}
              />
            </div>
          </div>
        </div>
      </Modal>

      <PayPointModal open={payPointModalOpen} onCancel={() => setPayPointModalOpen(false)} />
    </>
  )
}

export default CreditDetailModal
